import { Radians } from "@/libs/units/angle";
import type { DeviationSurveyEntryRows } from "@/composables/scenarioCalculations/calculationTypes";
import { extrapolateEntryRowAtMeasuredDepth } from "./extrapolation";

export const getDeviationSurveyPointsBetweenMeasuredDepths = (
  totalEntryRows: DeviationSurveyEntryRows,
  measuredDepthStart: number,
  measuredDepthEnd: number
): DeviationSurveyEntryRows => {
  if (
    measuredDepthStart < 0 ||
    measuredDepthEnd < 0 ||
    measuredDepthStart > measuredDepthEnd
  ) {
    return {
      data: [
        [0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0]
      ],
      surveyType: totalEntryRows.surveyType
    };
  }

  if (totalEntryRows.data.length) {
    // Get the index of deviation survey entry rows
    // which lie in between the given measured depths
    // i.e. closed interval
    const rowIndicies: number[] = [];

    for (let index = 0; index < totalEntryRows.data.length; index++) {
      const row = totalEntryRows.data[index];

      // Entry Rows are sorted, so we can break from loop after we reach the end
      if (row[0] > measuredDepthEnd) {
        break;
      }

      if (row[0] >= measuredDepthStart && row[0] <= measuredDepthEnd) {
        rowIndicies.push(index);
      }
    }

    const result = rowIndicies.map(i => totalEntryRows.data[i]);
    // If no entry rows were found between MDs then bail out.
    // But we still return fake entry rows at the given MDs
    if (!rowIndicies.length) {
      const closestStartRowByMD = totalEntryRows.data.reduce((prev, curr) =>
        Math.abs(curr[0] - measuredDepthStart) <
        Math.abs(prev[0] - measuredDepthStart)
          ? curr
          : prev
      );
      const closestEndRowByMD = totalEntryRows.data.reduce((prev, curr) =>
        Math.abs(curr[0] - measuredDepthEnd) <
        Math.abs(prev[0] - measuredDepthEnd)
          ? curr
          : prev
      );
      // Assume inclination and azimuth of closest rows
      const startInclinationDeg = closestStartRowByMD[1] ?? 0;
      const endInclinationDeg = closestEndRowByMD[1] ?? 0;
      const startAzimuthDeg = closestStartRowByMD[2] ?? 0;
      const endAzimuthDeg = closestEndRowByMD[2] ?? 0;

      const startTVDEstimate =
        Math.cos(Radians.toThisUnit(startInclinationDeg)) * measuredDepthStart;
      const endTVDEstimate =
        Math.cos(Radians.toThisUnit(endInclinationDeg)) * measuredDepthEnd;
      return {
        data: [
          [
            measuredDepthStart,
            startInclinationDeg,
            startAzimuthDeg,
            startTVDEstimate,
            0
          ],
          [
            measuredDepthEnd,
            endInclinationDeg,
            endAzimuthDeg,
            endTVDEstimate,
            0
          ]
        ],
        surveyType: totalEntryRows.surveyType
      };
    }

    const processBoundaryRow = (
      boundaryMD: number,
      rowIndex: number,
      isStart: boolean
    ): number[] => {
      const boundaryRow = totalEntryRows.data[rowIndex];
      let adjacentRow = totalEntryRows.data[rowIndex + (isStart ? -1 : 1)];
      if (!adjacentRow) {
        adjacentRow = [
          boundaryMD,
          boundaryRow[1],
          boundaryRow[2],
          boundaryRow[3],
          boundaryRow[4]
        ];
      }
      return extrapolateEntryRowAtMeasuredDepth(
        boundaryMD,
        isStart ? adjacentRow : boundaryRow,
        isStart ? boundaryRow : adjacentRow,
        totalEntryRows.surveyType
      );
    };

    if (totalEntryRows.data[rowIndicies[0]][0] > measuredDepthStart) {
      const startExtrapolation = processBoundaryRow(
        measuredDepthStart,
        rowIndicies[0],
        true
      );
      result.unshift(startExtrapolation);
    }

    if (
      totalEntryRows.data[rowIndicies[rowIndicies.length - 1]][0] <
      measuredDepthEnd
    ) {
      const endExtrapolation = processBoundaryRow(
        measuredDepthEnd,
        rowIndicies[rowIndicies.length - 1],
        false
      );
      result.push(endExtrapolation);
    }

    return {
      data: result,
      surveyType: totalEntryRows.surveyType
    };
  }

  return {
    data: [
      [measuredDepthStart, 0, 0, 0, 0],
      [measuredDepthEnd, 0, 0, 0, 0]
    ],
    surveyType: totalEntryRows.surveyType
  };
};

export const getDeviationSurveyPointsBetweenVerticalDepths = (
  totalEntryRows: DeviationSurveyEntryRows,
  verticalDepthStart: number,
  verticalDepthEnd: number
): number[][] => {
  if (totalEntryRows.data.length) {
    // Get the index of deviation survey entry rows
    // which lie in between the given measured depths
    // i.e. closed interval
    const rowIndicies: number[] = [];

    for (let index = 0; index < totalEntryRows.data.length; index++) {
      const row = totalEntryRows.data[index];

      // Entry Rows are sorted, so we can break from loop after we reach the end
      if (row[3] > verticalDepthEnd) {
        break;
      }

      if (row[3] >= verticalDepthStart && row[3] <= verticalDepthEnd) {
        rowIndicies.push(index);
      }
    }

    const result = rowIndicies.map(i => totalEntryRows.data[i]);

    // Clamp the ends of the deviation survey using MD start and MD end
    // by extrapolating an entry row at each boundry.
    // If the deviation survey already contains a row at either
    // lower or upper boundary, then we do not need to extrapolate
    // because it is already included in the result

    const firstRowInBounds = totalEntryRows.data[rowIndicies[0]];
    if (firstRowInBounds[3] > verticalDepthStart) {
      // First item in the bounded entry rows has a measured depth greater than the provided measuredDepthStart
      // Get the row before it
      const rowBelowBounds = totalEntryRows.data[rowIndicies[0] - 1];

      if (rowBelowBounds) {
        // Extrapolate a new row based on boundary
        const entryRowAtMeasuredDepth = extrapolateEntryRowAtMeasuredDepth(
          verticalDepthStart,
          rowBelowBounds,
          totalEntryRows.data[rowIndicies[0]],
          totalEntryRows.surveyType
        );
        // We will add that first item with a newly calculated entry row based on measuredDepthStart
        result.unshift(entryRowAtMeasuredDepth);
      }
    }

    const lastRowInBounds =
      totalEntryRows.data[rowIndicies[rowIndicies.length - 1]];
    if (lastRowInBounds[3] < verticalDepthEnd) {
      // Last item in the bounded entry rows has a measured depth less than the provided measuredDepthEnd
      // Get the row after it
      const rowAboveBounds =
        totalEntryRows.data[rowIndicies[rowIndicies.length - 1] + 1];

      if (rowAboveBounds) {
        // Extrapolate a new row based on boundary
        const entryRowAtMeasuredDepth = extrapolateEntryRowAtMeasuredDepth(
          verticalDepthEnd,
          rowAboveBounds,
          totalEntryRows.data[rowIndicies[rowIndicies.length - 1]],
          totalEntryRows.surveyType
        );
        // We will add that first item with a newly calculated entry row based on measuredDepthEnd
        result.push(entryRowAtMeasuredDepth);
      }
    }

    return result;
  }

  return [
    [verticalDepthStart, 0, 0, 0, 0],
    [verticalDepthEnd, 0, 0, 0, 0]
  ];
};
